import AdFoxHelper from '../helpers/AdFoxHelper';

const RESIZE_DEBOUNCE_MS = 300;
const PLACEMENT_TYPES = {
    BR1: 'br1',
    BR2: 'br2'
};
const MIN_SCREEN_WIDTH_FOR_SIDEBARS = 2011;
const HAS_PROMO_CLASS = 'has-promo-placements';
const CONTAINER_SELECTORS = {
    'a1': '.promo-placement--below-search',
    'a51': '.promo-placement__in-feed',
    'br1': '.promo-placement--sidebar-left',
    'br2': '.promo-placement--sidebar-right'
};

function createPromoPlacementManager() {
    const adFoxHelper = AdFoxHelper.getInstance();

    let currentCountryId = null;
    let activePlacements = [];
    let promoPlacements = [];
    let ownerId = null;
    let resizeDebounceTimer = null;
    let isResizeListenerAdded = false;
    let resizeHandler = null;
    let isDestroyed = false;
    const hiddenSidebarPlacements = new Set();
    let allContainerIds = [];
    let containerObserver = null;
    let isCreatingPlacements = false;

    function getPlacementType(placementId) {
        if (!placementId) return '';
        const parts = placementId.split('.');
        if (parts.length < 3) return '';
        return parts[2].toLowerCase();
    }

    function isSidebarPlacement(placementId) {
        const type = getPlacementType(placementId);
        return type === PLACEMENT_TYPES.BR1 || type === PLACEMENT_TYPES.BR2;
    }

    function isA51Placement(placementId) {
        return getPlacementType(placementId) === 'a51';
    }

    function findContainerByType(placementId) {
        const type = getPlacementType(placementId);
        const selector = CONTAINER_SELECTORS[type];
        return selector ? document.querySelector(selector) : null;
    }

    function syncContainerIdsForCountry(countryId) {
        const placementsForCountry = promoPlacements.filter(p => Number(p.countryId) === countryId);
        const seenTypes = new Set();
        placementsForCountry.forEach(placement => {
            const type = getPlacementType(placement.placementId);
            if (seenTypes.has(type)) return;
            seenTypes.add(type);
            const container = findContainerByType(placement.placementId);
            if (container && container.id !== placement.containerId) {
                container.id = placement.containerId;
            }
        });
    }

    function hasContainersInDom() {
        return Object.values(CONTAINER_SELECTORS).some(
            selector => document.querySelector(selector)
        );
    }

    function updateFluidWidth() {
        const searchView = document.querySelector('.forSearchView');
        if (!searchView) return;

        const hasVisibleSidebars = activePlacements.some(p =>
            isSidebarPlacement(p.placementId) &&
            p.isCreated &&
            !hiddenSidebarPlacements.has(p.placementId)
        );

        if (hasVisibleSidebars) {
            searchView.classList.add(HAS_PROMO_CLASS);
        } else {
            searchView.classList.remove(HAS_PROMO_CLASS);
        }
    }

    function handleSidebarError(placementId) {
        hiddenSidebarPlacements.add(placementId);
        updateFluidWidth();
    }

    function hasEnoughWidthForSidebars() {
        return window.innerWidth >= MIN_SCREEN_WIDTH_FOR_SIDEBARS;
    }

    function cancelContainerObserver() {
        if (containerObserver) {
            containerObserver.disconnect();
            containerObserver = null;
        }
    }

    function createPlacement(placement, hasWidthForSidebars = true) {
        if (isDestroyed) return;

        const { placementId, customParam1, customParam2 } = placement;
        let containerId = placement.containerId;
        let container = document.querySelector(`#${containerId}`);

        if (!container) {
            console.warn(`[PromoPlacementManager] Container ${placement.containerId} not found`);
            return;
        }

        if (!customParam1 || !customParam2) {
            console.error(`[PromoPlacementManager] Missing customParam1 or customParam2 for ${placementId}`, {
                customParam1,
                customParam2,
                customParam1Type: typeof customParam1,
                customParam2Type: typeof customParam2
            });
            return;
        }

        if (isSidebarPlacement(placementId) && !hasWidthForSidebars) {
            activePlacements.push({ ...placement, containerId, isCreated: false });
            return;
        }

        const success = adFoxHelper.create({
            containerId,
            customParam1,
            customParam2,
            ownerId,
            placementId,
            onSidebarError: handleSidebarError,
            countryId: currentCountryId
        });

        if (success) {
            activePlacements.push({ ...placement, containerId, isCreated: true });
        } else {
            console.error(`[PromoPlacementManager] Failed to create ${placementId}`);
        }
    }

    function destroyPlacements() {
        activePlacements.forEach(placement => {
            adFoxHelper.clear(placement.containerId);
        });
        activePlacements = [];
        hiddenSidebarPlacements.clear();
        isCreatingPlacements = false;
        updateFluidWidth();
    }

    function clearAllContainers() {
        if (!allContainerIds || allContainerIds.length === 0) return;
        allContainerIds.forEach(containerId => {
            adFoxHelper.clear(containerId);
        });
    }

    function handleResize() {
        if (activePlacements.length === 0 || isDestroyed) return;

        const hasWidth = hasEnoughWidthForSidebars();

        activePlacements.forEach(placement => {
            if (!isSidebarPlacement(placement.placementId)) return;

            const container = document.querySelector(`#${placement.containerId}`);
            if (!container) return;

            if (!placement.isCreated && hasWidth) {
                const success = adFoxHelper.create({
                    containerId: placement.containerId,
                    customParam1: placement.customParam1,
                    customParam2: placement.customParam2,
                    ownerId,
                    placementId: placement.placementId,
                    onSidebarError: handleSidebarError,
                    countryId: currentCountryId
                });
                if (success) {
                    placement.isCreated = true; // eslint-disable-line no-param-reassign
                }
            } else if (placement.isCreated) {
                const isHidden = container.style.display === 'none';
                if (hasWidth && isHidden) {
                    adFoxHelper.show(placement.containerId);
                } else if (!hasWidth && !isHidden) {
                    adFoxHelper.hide(placement.containerId);
                }
            }
        });
    }

    function setupResizeListener() {
        if (isResizeListenerAdded || isDestroyed) return;

        resizeHandler = () => {
            clearTimeout(resizeDebounceTimer);
            resizeDebounceTimer = setTimeout(handleResize, RESIZE_DEBOUNCE_MS);
        };

        window.addEventListener('resize', resizeHandler);
        isResizeListenerAdded = true;
    }

    function createPlacementsForCountry(countryId) {
        if (isDestroyed) return;

        const id = Number(countryId);
        const placementsForCountry = promoPlacements.filter(p => Number(p.countryId) === id);

        if (placementsForCountry.length === 0) {
            clearAllContainers();
            updateFluidWidth();
            return;
        }

        const hasWidth = hasEnoughWidthForSidebars();
        placementsForCountry
            .filter((placement) => !isA51Placement(placement.placementId))
            .forEach(placement => createPlacement(placement, hasWidth));
        updateFluidWidth();
    }

    function createPlacementsWhenContainersReady(countryId) {
        if (isDestroyed) return;

        cancelContainerObserver();
        isCreatingPlacements = true;

        try {
            const id = Number(countryId);
            const placementsForCountry = promoPlacements.filter(p => Number(p.countryId) === id);

            if (placementsForCountry.length === 0) {
                clearAllContainers();
                updateFluidWidth();
                isCreatingPlacements = false;
                return;
            }

            syncContainerIdsForCountry(id);

            const registeredPlacements = [];
            const unregisteredPlacements = [];

            placementsForCountry.forEach(placement => {
                if (adFoxHelper.isRegistered(placement.containerId)) {
                    registeredPlacements.push(placement);
                } else {
                    unregisteredPlacements.push(placement);
                }
            });

            const deferredFromRegistered = [];

            if (registeredPlacements.length > 0) {
                const hasWidth = hasEnoughWidthForSidebars();
                registeredPlacements.forEach(placement => {
                    const inDom = document.querySelector(`#${placement.containerId}`);
                    if (inDom) {
                        createPlacement(placement, hasWidth);
                    } else if (getPlacementType(placement.placementId) !== 'a51') {
                        deferredFromRegistered.push(placement);
                    }
                });
                updateFluidWidth();
            }

            if (unregisteredPlacements.length > 0 || deferredFromRegistered.length > 0) {
                const hasWidth = hasEnoughWidthForSidebars();
                const deferredPlacements = [...deferredFromRegistered];

                unregisteredPlacements.forEach(placement => {
                    const inDom = document.querySelector(`#${placement.containerId}`);
                    if (inDom) {
                        createPlacement(placement, hasWidth);
                    } else if (getPlacementType(placement.placementId) !== 'a51') {
                        deferredPlacements.push(placement);
                    }
                });

                updateFluidWidth();

                if (deferredPlacements.length === 0) {
                    isCreatingPlacements = false;
                    return;
                }

                const hasDeferredContainers = () => deferredPlacements.every(
                    p => document.querySelector(`#${p.containerId}`)
                );

                if (hasDeferredContainers()) {
                    const width = hasEnoughWidthForSidebars();
                    deferredPlacements.forEach(p => createPlacement(p, width));
                    updateFluidWidth();
                    isCreatingPlacements = false;
                    return;
                }

                containerObserver = new MutationObserver(() => {
                    if (isDestroyed) {
                        cancelContainerObserver();
                        isCreatingPlacements = false;
                        return;
                    }
                    if (hasDeferredContainers()) {
                        cancelContainerObserver();
                        const width = hasEnoughWidthForSidebars();
                        deferredPlacements.forEach(p => createPlacement(p, width));
                        updateFluidWidth();
                        isCreatingPlacements = false;
                    }
                });

                containerObserver.observe(document.body, { childList: true, subtree: true });
                return;
            }

            isCreatingPlacements = false;
        } catch (error) {
            console.error('[PromoPlacementManager] Error in createPlacementsWhenContainersReady:', error);
            isCreatingPlacements = false;
        }
    }

    function extractCountryId(tourCriteria) {
        try {
            const countries = tourCriteria.get('countries');
            if (!countries || countries.length === 0) return null;
            const countryId = countries.at(0).get('id');
            return countryId || null;
        } catch (error) {
            console.error('[PromoPlacementManager] Error extracting country ID:', error);
            return null;
        }
    }

    function processSearchStart(tourCriteria) {
        try {
            const newCountryId = extractCountryId(tourCriteria);

            if (!newCountryId) {
                console.warn('[PromoPlacementManager] Country ID not found');
                if (currentCountryId !== null || activePlacements.length > 0) {
                    destroyPlacements();
                    currentCountryId = null;
                    isCreatingPlacements = false;
                }
                return;
            }

            const newId = Number(newCountryId);
            const newPlacements = promoPlacements.filter(p => Number(p.countryId) === newId);
            const isCountryChanged = currentCountryId !== null && currentCountryId !== newId;

            if (isCountryChanged) {
                activePlacements.forEach(p => adFoxHelper.clear(p.containerId));
            }

            activePlacements = [];
            hiddenSidebarPlacements.clear();

            if (newPlacements.length === 0) {
                currentCountryId = newId;
                updateFluidWidth();
                return;
            }

            currentCountryId = newId;
            updateFluidWidth();
            createPlacementsWhenContainersReady(newId);
        } catch (error) {
            console.error('[PromoPlacementManager] Error in onSearchStart:', error);
        }
    }

    function init(newPromoPlacements, newOwnerId) {
        if (isDestroyed) {
            console.error('[PromoPlacementManager] Cannot init destroyed instance');
            return;
        }

        if (!Array.isArray(newPromoPlacements)) {
            console.error('[PromoPlacementManager] promoPlacements must be an array');
            return;
        }

        if (!newOwnerId) {
            console.error('[PromoPlacementManager] ownerId is required');
            return;
        }

        promoPlacements = newPromoPlacements;
        ownerId = newOwnerId;
        allContainerIds = [...new Set(newPromoPlacements.map(p => p.containerId))];
        setupResizeListener();
    }

    function createA51Placement() {
        if (isDestroyed) return;

        const placementForCurrentCountry = promoPlacements.find(
            p => Number(p.countryId) === Number(currentCountryId) && isA51Placement(p.placementId)
        );
        const a51Selector = CONTAINER_SELECTORS['a51'];
        const a51Container = a51Selector ? document.querySelector(a51Selector) : null;

        if (placementForCurrentCountry && a51Container && a51Container.id !== placementForCurrentCountry.containerId) {
            a51Container.id = placementForCurrentCountry.containerId;
        }

        if (!currentCountryId) {
            if (a51Container && a51Container.id) {
                adFoxHelper.showLoader(a51Container.id);
            }
            return;
        }

        const alreadyActive = activePlacements.some(
            p => isA51Placement(p.placementId) && p.isCreated
        );
        if (alreadyActive) {
            const isStuck = a51Container
                && adFoxHelper.containerStates.get(a51Container.id) === 'loading';
            if (!isStuck) return;
            activePlacements = activePlacements.filter(p => !isA51Placement(p.placementId));
            adFoxHelper.clear(a51Container.id);
        }
        cancelContainerObserver();
        if (placementForCurrentCountry) {
            if (a51Container && a51Container.id) {
                adFoxHelper.showLoader(a51Container.id);
            }
            createPlacement(placementForCurrentCountry);
        }
    }

    function reset() {
        cancelContainerObserver();
        destroyPlacements();
        currentCountryId = null;
        isCreatingPlacements = false;
    }

    function destroy() {
        if (isDestroyed) return;

        cancelContainerObserver();
        destroyPlacements();

        if (resizeHandler && isResizeListenerAdded) {
            window.removeEventListener('resize', resizeHandler);
            isResizeListenerAdded = false;
            resizeHandler = null;
        }

        clearTimeout(resizeDebounceTimer);

        const searchView = document.querySelector('.forSearchView');
        if (searchView) {
            searchView.classList.remove(HAS_PROMO_CLASS);
        }

        currentCountryId = null;
        promoPlacements = [];
        ownerId = null;
        hiddenSidebarPlacements.clear();
        isCreatingPlacements = false;
        isDestroyed = true;
    }

    return {
        get ownerId() { return ownerId; },
        get currentCountryId() { return currentCountryId; },
        set currentCountryId(id) { currentCountryId = id; },
        get activePlacements() { return activePlacements; },
        set activePlacements(arr) { activePlacements = arr; },
        get promoPlacements() { return promoPlacements; },
        get isCreatingPlacements() { return isCreatingPlacements; },
        get adFoxHelper() { return adFoxHelper; },
        init,
        onSearchStart: (tourCriteria) => { if (!isDestroyed) processSearchStart(tourCriteria); },
        createPlacementsForCountry,
        createPlacementsWhenContainersReady,
        createPlacement,
        destroyPlacements,
        handleResize,
        extractCountryId,
        getPlacementType,
        isSidebarPlacement,
        hasEnoughWidthForSidebars,
        hasContainersInDom,
        setCurrentCountryId: (id) => { currentCountryId = id; },
        getCurrentCountryId: () => currentCountryId,
        getActivePlacements: () => [...activePlacements],
        createA51Placement,
        reset,
        destroy
    };
}

let promoPlacementManagerInstance = null;

export default {
    getInstance() {
        if (!promoPlacementManagerInstance) {
            promoPlacementManagerInstance = createPromoPlacementManager();
        }
        return promoPlacementManagerInstance;
    },

    resetInstance() {
        if (promoPlacementManagerInstance) {
            promoPlacementManagerInstance.destroy();
            promoPlacementManagerInstance = null;
        }
    }
};
